// /opt/ptg01-v1/packages/ui/src/CountrySelect.tsx
// Sélecteur pays/nationalité avec drapeaux — partagé entre front-admin + front-extranet.
// 'use client' : utilise useState/useEffect/DOM events.
'use client';
import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import * as Flags from 'country-flag-icons/react/3x2';

// Country code → Country name → Nationality
const COUNTRIES: { code: string; name: string; nationality: string }[] = [
    { code: 'FR', name: 'France', nationality: 'Française' },
    { code: 'DE', name: 'Allemagne', nationality: 'Allemande' },
    { code: 'DZ', name: 'Algérie', nationality: 'Algérienne' },
    { code: 'SA', name: 'Arabie Saoudite', nationality: 'Saoudienne' },
    { code: 'AR', name: 'Argentine', nationality: 'Argentine' },
    { code: 'AU', name: 'Australie', nationality: 'Australienne' },
    { code: 'BE', name: 'Belgique', nationality: 'Belge' },
    { code: 'BJ', name: 'Bénin', nationality: 'Béninoise' },
    { code: 'BR', name: 'Brésil', nationality: 'Brésilienne' },
    { code: 'BF', name: 'Burkina Faso', nationality: 'Burkinabè' },
    { code: 'BI', name: 'Burundi', nationality: 'Burundaise' },
    { code: 'CM', name: 'Cameroun', nationality: 'Camerounaise' },
    { code: 'CA', name: 'Canada', nationality: 'Canadienne' },
    { code: 'CF', name: 'Centrafrique', nationality: 'Centrafricaine' },
    { code: 'CN', name: 'Chine', nationality: 'Chinoise' },
    { code: 'KM', name: 'Comores', nationality: 'Comorienne' },
    { code: 'CG', name: 'Congo', nationality: 'Congolaise' },
    { code: 'CD', name: 'Congo (RDC)', nationality: 'Congolaise (RDC)' },
    { code: 'KR', name: 'Corée du Sud', nationality: 'Sud-Coréenne' },
    { code: 'CI', name: "Côte d'Ivoire", nationality: 'Ivoirienne' },
    { code: 'DK', name: 'Danemark', nationality: 'Danoise' },
    { code: 'DJ', name: 'Djibouti', nationality: 'Djiboutienne' },
    { code: 'EG', name: 'Égypte', nationality: 'Égyptienne' },
    { code: 'ES', name: 'Espagne', nationality: 'Espagnole' },
    { code: 'US', name: 'États-Unis', nationality: 'Américaine' },
    { code: 'FI', name: 'Finlande', nationality: 'Finlandaise' },
    { code: 'GA', name: 'Gabon', nationality: 'Gabonaise' },
    { code: 'GR', name: 'Grèce', nationality: 'Grecque' },
    { code: 'GN', name: 'Guinée', nationality: 'Guinéenne' },
    { code: 'GQ', name: 'Guinée Équatoriale', nationality: 'Équato-guinéenne' },
    { code: 'HT', name: 'Haïti', nationality: 'Haïtienne' },
    { code: 'IN', name: 'Inde', nationality: 'Indienne' },
    { code: 'IE', name: 'Irlande', nationality: 'Irlandaise' },
    { code: 'IT', name: 'Italie', nationality: 'Italienne' },
    { code: 'JP', name: 'Japon', nationality: 'Japonaise' },
    { code: 'LB', name: 'Liban', nationality: 'Libanaise' },
    { code: 'LU', name: 'Luxembourg', nationality: 'Luxembourgeoise' },
    { code: 'MG', name: 'Madagascar', nationality: 'Malgache' },
    { code: 'ML', name: 'Mali', nationality: 'Malienne' },
    { code: 'MA', name: 'Maroc', nationality: 'Marocaine' },
    { code: 'MU', name: 'Maurice', nationality: 'Mauricienne' },
    { code: 'MR', name: 'Mauritanie', nationality: 'Mauritanienne' },
    { code: 'NE', name: 'Niger', nationality: 'Nigérienne' },
    { code: 'NL', name: 'Pays-Bas', nationality: 'Néerlandaise' },
    { code: 'NO', name: 'Norvège', nationality: 'Norvégienne' },
    { code: 'PL', name: 'Pologne', nationality: 'Polonaise' },
    { code: 'PT', name: 'Portugal', nationality: 'Portugaise' },
    { code: 'RO', name: 'Roumanie', nationality: 'Roumaine' },
    { code: 'GB', name: 'Royaume-Uni', nationality: 'Britannique' },
    { code: 'RU', name: 'Russie', nationality: 'Russe' },
    { code: 'RW', name: 'Rwanda', nationality: 'Rwandaise' },
    { code: 'SN', name: 'Sénégal', nationality: 'Sénégalaise' },
    { code: 'SE', name: 'Suède', nationality: 'Suédoise' },
    { code: 'CH', name: 'Suisse', nationality: 'Suisse' },
    { code: 'TD', name: 'Tchad', nationality: 'Tchadienne' },
    { code: 'TG', name: 'Togo', nationality: 'Togolaise' },
    { code: 'TN', name: 'Tunisie', nationality: 'Tunisienne' },
    { code: 'TR', name: 'Turquie', nationality: 'Turque' },
    { code: 'UA', name: 'Ukraine', nationality: 'Ukrainienne' },
];

// Sort: France first, then alphabetical
const SORTED = [...COUNTRIES].sort((a, b) => {
    if (a.code === 'FR') return -1;
    if (b.code === 'FR') return 1;
    return a.name.localeCompare(b.name, 'fr');
});

function FlagIcon({ code, className }: { code: string; className?: string }) {
    const Flag = (Flags as Record<string, React.ComponentType<{ className?: string }>>)[code];
    if (!Flag) return <div className={`bg-slate-200 rounded-sm ${className}`} />;
    return <Flag className={`rounded-sm shadow-sm ${className}`} />;
}

interface CountrySelectProps {
    value: string;
    onChange: (value: string) => void;
    type?: 'country' | 'nationality';
    hasError?: boolean;
}

export default function CountrySelect({ value, onChange, type = 'country', hasError }: CountrySelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Resolve current selection
    const resolved = COUNTRIES.find(c =>
        type === 'nationality' ? c.nationality === value : c.name === value
    ) || COUNTRIES[0];

    const displayLabel = type === 'nationality' ? resolved.nationality : resolved.name.toUpperCase();

    const filtered = SORTED.filter(c => {
        const label = type === 'nationality' ? c.nationality : c.name;
        return label.toLowerCase().includes(search.toLowerCase());
    });

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setOpen(!open)}
                className={`w-full px-3 py-2 text-sm rounded-lg border ${hasError ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'} text-slate-900 flex items-center gap-2 text-left`}>
                <FlagIcon code={resolved.code} className="w-5 h-3.5 shrink-0" />
                <span className="flex-1 truncate">{displayLabel}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            className="w-full px-2 py-1.5 text-xs rounded border border-slate-200 outline-none focus:border-[#000091]"
                            placeholder="Rechercher..." autoFocus />
                    </div>
                    <div className="overflow-y-auto max-h-48">
                        {filtered.map(c => {
                            const label = type === 'nationality' ? c.nationality : c.name;
                            const isSelected = resolved.code === c.code;
                            return (
                                <button key={c.code} type="button"
                                    onClick={() => {
                                        onChange(type === 'nationality' ? c.nationality : c.name);
                                        setOpen(false);
                                        setSearch('');
                                    }}
                                    className={`w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50 ${isSelected ? 'bg-blue-50 text-[#000091] font-semibold' : 'text-slate-700'}`}>
                                    <FlagIcon code={c.code} className="w-5 h-3.5 shrink-0" />
                                    {type === 'nationality' ? label : label.toUpperCase()}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
